import type { Category, MenuItem } from 'src/types/types';

export type SubcategoryGroup = {
  category: Category;
  items: MenuItem[];
};

export type GroupedCategory = {
  category: Category;
  directItems: MenuItem[];
  subgroups: SubcategoryGroup[];
};

export const groupMenuItems = (
  categories: Category[],
  menuItems: MenuItem[],
): GroupedCategory[] => {
  const itemsByCategory = new Map<string, MenuItem[]>();

  for (const item of menuItems) {
    const list = itemsByCategory.get(item.categoryId) ?? [];
    list.push(item);
    itemsByCategory.set(item.categoryId, list);
  }

  const topCategories = categories
    .filter((c) => c.parentId === null)
    .sort((a, b) => a.order - b.order);

  const subcategoriesByParent = new Map<string, Category[]>();
  for (const category of categories) {
    if (category.parentId === null) continue;
    const list = subcategoriesByParent.get(category.parentId) ?? [];
    list.push(category);
    subcategoriesByParent.set(category.parentId, list);
  }

  return topCategories
    .map((category) => {
      const categoryItems = (itemsByCategory.get(category.id) ?? []).sort(
        (a, b) => a.order - b.order,
      );

      const subcategories = (subcategoriesByParent.get(category.id) ?? []).sort(
        (a, b) => a.order - b.order,
      );

      const validSubcategoryIds = new Set(subcategories.map((s) => s.id));

      const directItems = categoryItems.filter(
        (item) =>
          !item.subcategoryId || !validSubcategoryIds.has(item.subcategoryId),
      );

      const subgroups = subcategories
        .map((subcategory) => ({
          category: subcategory,
          items: categoryItems.filter(
            (item) => item.subcategoryId === subcategory.id,
          ),
        }))
        .filter((group) => group.items.length > 0);

      return { category, directItems, subgroups };
    })
    .filter(
      (group) => group.directItems.length > 0 || group.subgroups.length > 0,
    );
};
